import { getFuelFromTags } from 'helpers/filterComponent.helper/filterComponent.helper'
import { debugFilterStore } from 'store/filter/filter'

export type DatasetAndItem = {
  item: UiConfigPropsItem
  dataset?: Dataset
}

export function itemHasHiddenDefaultTag(
  item: UiConfigPropsItem,
  hiddenDefaultTags: string[],
  activeGroupingTag: string
): boolean {
  const itemTags = item.tags ?? []
  //activeGroupingTag is always top level tag, so we check if tag in itemTags starts with activeGroupingTag and if it exists in hiddenDefaultTags TODO: if we no longer only group on top level we need to change this
  if (itemTags.length !== 0 && activeGroupingTag !== '') {
    return itemTags.some((tag) => tag.startsWith(activeGroupingTag) && hiddenDefaultTags.includes(tag))
  }
  return false
}

export function useGrouping(
  itemsMergedWithTags: UiConfigPropsItem[],
  uiConfigProps: UiConfigProps,
  datasets: Dataset[],
  uid: number | undefined,
  tagsHiddenFromGrouping: string[],
  groupingTag: string,
  overriddenGroupingTag?: string,
  tags_config?: Record<string, TagsConfig>
): { datasets: Dataset[] | undefined; items: UiConfigPropsItem[] | undefined } {
  const activeGroupingTag = overriddenGroupingTag ?? groupingTag
  if (activeGroupingTag && itemsMergedWithTags && uiConfigProps?.include_items_in_grouping && datasets.length > 0) {
    //if uiconfig has items with tags and is supposed to be included in aggregation/grouping
    const itemsExcludedFromGraph = itemsMergedWithTags.filter((item) =>
      itemHasHiddenDefaultTag(item, tagsHiddenFromGrouping, activeGroupingTag)
    )
    const itemsIncludedInGrouping = itemsMergedWithTags.filter(
      (item) =>
        !item.exclude_from_grouping && item?.tags && item?.tags?.length !== 0 && !itemsExcludedFromGraph.includes(item)
    ) //only include items in grouping if they have any tags at all, or if they do not have the attribute 'exclude_from_grouping'
    const datasetsIncludedInGrouping = datasets.filter((dataset) =>
      itemsIncludedInGrouping.some((item) => item.data_id === dataset.return_id)
    )

    const itemsExcludedFromGrouping = itemsMergedWithTags.filter(
      (item) => !itemsIncludedInGrouping.includes(item) && !itemsExcludedFromGraph.includes(item)
    ) //items not included in grouping should still be visible in graphs
    const datasetsExcludedFromGrouping = datasets.filter((dataset) =>
      itemsExcludedFromGrouping.some((item) => item.data_id === dataset.return_id)
    )

    const { datasets: groupedDatasets, items: groupedItems } = getGroupedDatasetsAndNewItems({
      uiConfigProps,
      datasets: datasetsIncludedInGrouping,
      itemsWithTags: itemsIncludedInGrouping,
      activeGroupingTag,
      uid,
      tags_config,
    })

    //only items with tags and tags not with exclude_from_grouping
    const mergedDatasets = groupedDatasets.concat(datasetsExcludedFromGrouping)
    const mergedItems = groupedItems.concat(itemsExcludedFromGrouping)

    return { datasets: mergedDatasets, items: mergedItems }
  } else {
    return { datasets: undefined, items: undefined }
  }
}
//get a collection of datasets and items per top level tag (which is then used to sum up the datasets and create new items and datasets)
export function getAllItemsAndDatasetsBasedOnDatasetReturnId(
  groupingTag: string,
  itemsWithTags: UiConfigPropsItem[],
  datasets: Dataset[]
): { [subTag: string]: { [returnId: string]: DatasetAndItem } } {
  // Pre-build dataset lookup map for O(1) access instead of O(n) find() calls
  const datasetMap = new Map<string, Dataset>()
  for (const dataset of datasets) {
    datasetMap.set(dataset.return_id, dataset)
  }

  const allSubTagsWithItemAndDataset: { [subTag: string]: { [returnId: string]: DatasetAndItem } } = {}

  for (const item of itemsWithTags) {
    const itemTags = item.tags
    if (!itemTags) continue

    for (const tag of itemTags) {
      const slashIndex = tag.indexOf('/')
      if (slashIndex === -1) continue

      const topLevelTag = tag.substring(0, slashIndex)
      if (topLevelTag !== groupingTag) continue

      const subTag = tag.substring(slashIndex + 1)
      if (!subTag) continue

      // Create groupTag key - combine subTag with grouping_type if present
      const groupTag = item.grouping_type ? `${subTag}&&${item.grouping_type}` : subTag

      // Initialize nested object if needed
      if (!allSubTagsWithItemAndDataset[groupTag]) {
        allSubTagsWithItemAndDataset[groupTag] = {}
      }

      // O(1) lookup instead of O(n) find()
      allSubTagsWithItemAndDataset[groupTag][item.data_id] = {
        item: item,
        dataset: datasetMap.get(item.data_id),
      }
    }
  }

  return allSubTagsWithItemAndDataset
}

interface DatasetsAndItemsPerTagOptions {
  activeGroupingTag: string
  allSubTagsWithItemAndDataset: { [subTag: string]: { [returnId: string]: DatasetAndItem } }
  prefix?: string
  tags_config?: Record<string, TagsConfig>
}

//group(sum) datasets and create new items and datasets per subtag
export function getDatasetsAndItemsPerTag({
  activeGroupingTag,
  allSubTagsWithItemAndDataset,
  prefix,
  tags_config,
}: DatasetsAndItemsPerTagOptions): { newDatasets: Dataset[]; newItems: UiConfigPropsItem[] } {
  const subTagKeys = Object.keys(allSubTagsWithItemAndDataset)
  const newSumDatasetsForThisTag: Dataset[] = []
  const newSumUiConfigItemsForThisTag: UiConfigPropsItem[] = []

  for (const subTag of subTagKeys) {
    const subTagItems = allSubTagsWithItemAndDataset[subTag]
    const subTagItemKeys = Object.keys(subTagItems)

    // Find first item with both dataset and item using early exit
    let firstItem: DatasetAndItem | undefined
    for (const key of subTagItemKeys) {
      const candidate = subTagItems[key]
      if (candidate.dataset && candidate.item) {
        firstItem = candidate
        break
      }
    }

    if (!firstItem?.dataset) continue

    const dataId = prefix ? `${prefix}sum_of_${subTag}` : `sum_of_${subTag}`
    const tag = `${activeGroupingTag}/${subTag}`

    // Create new item - extract grouping_type from subTag if present
    const groupingTypeSeparatorIndex = subTag.indexOf('&&')
    const newUiConfigSumItem: UiConfigPropsItem = {
      ...firstItem.item,
      data_id: dataId,
      tags: [tag],
      color: tags_config?.[tag]?.color ?? firstItem.item.color,
      title: groupingTypeSeparatorIndex !== -1 ? subTag.substring(0, groupingTypeSeparatorIndex) : subTag,
      fuel_type: firstItem.item.grouping_type ? getFuelFromTags(firstItem.item.tags) : null,
    }

    // Collect datasets to sum
    const datasetsToSum: Dataset[] = []
    for (const key of subTagItemKeys) {
      const item = subTagItems[key]
      if (item.dataset) {
        datasetsToSum.push(item.dataset)
      }
    }

    const newGroupedDataset = sumElementWiseDatasetsToNewDataset(dataId, firstItem.dataset, datasetsToSum)
    newSumDatasetsForThisTag.push(newGroupedDataset)
    newSumUiConfigItemsForThisTag.push(newUiConfigSumItem)
  }

  return { newDatasets: newSumDatasetsForThisTag, newItems: newSumUiConfigItemsForThisTag }
}

//sum datasets element wise and create new dataset based on the summation
export function sumElementWiseDatasetsToNewDataset(
  returnIdOfNewDataset: string,
  firstDataset: Dataset,
  allDatasetsToBeSummed: Dataset[]
): Dataset {
  const timesLength = firstDataset.times.length

  // Pre-allocate array with zeros for better performance
  const newValues = new Array<number>(timesLength).fill(0)

  // Sum all datasets element-wise
  for (const dataset of allDatasetsToBeSummed) {
    const datasetValues = dataset.values
    for (let i = 0; i < timesLength; i++) {
      const value = datasetValues[i]
      if (value != null) {
        newValues[i] += value
      }
    }
  }

  return {
    return_id: returnIdOfNewDataset,
    times: firstDataset.times ?? [],
    values: newValues,
  }
}

type GroupedDatasetsAndNewItemsOptions = {
  uiConfigProps: UiConfigProps
  datasets: Dataset[]
  itemsWithTags: UiConfigPropsItem[]
  activeGroupingTag: string
  uid?: number
  tags_config?: Record<string, TagsConfig>
}

// returns new datasets and items based on the grouping of other datasets and items
export function getGroupedDatasetsAndNewItems({
  uiConfigProps,
  datasets,
  itemsWithTags,
  activeGroupingTag,
  uid,
  tags_config,
}: GroupedDatasetsAndNewItemsOptions): { datasets: Dataset[]; items: UiConfigPropsItem[] } {
  let baseItemsHasPrefix = false
  let returnIdPrefixes: string[] = []
  if (uiConfigProps.multi_base_items_override) {
    //if we use multi_base_items_override we need to group by the return_id_prefix (e.g Horizontal bars in follow-up)

    returnIdPrefixes = uiConfigProps.multi_base_items_override.map((item) => item.return_id_prefix) as string[]

    //unsure what happens if only one has a prefix, and the other one is undefined. Will they not group?
    if (returnIdPrefixes.some((prefix) => prefix !== undefined)) {
      baseItemsHasPrefix = true
    }
  }

  if (baseItemsHasPrefix) {
    const allNewDatasets: Dataset[] = []
    const allNewItems: UiConfigPropsItem[] = []

    returnIdPrefixes.forEach((prefix) => {
      const itemsWithPrefix = itemsWithTags.filter((item) => item.data_id.startsWith(prefix))
      const datasetWithPrefix = datasets.filter((dataset) => dataset.return_id.startsWith(prefix))
      const allSubTagsWithItemAndDataset: { [subTag: string]: { [returnId: string]: DatasetAndItem } } =
        getAllItemsAndDatasetsBasedOnDatasetReturnId(activeGroupingTag, itemsWithPrefix, datasetWithPrefix)

      const { newDatasets: newSumDatasets, newItems: newSumItems } = getDatasetsAndItemsPerTag({
        activeGroupingTag,
        allSubTagsWithItemAndDataset,
        prefix,
        tags_config,
      })

      allNewDatasets.push(...newSumDatasets)
      allNewItems.push(...newSumItems)
    })

    return { datasets: allNewDatasets, items: allNewItems }
  } else {
    const allSubTagsWithItemAndDataset: { [subTag: string]: { [returnId: string]: DatasetAndItem } } =
      getAllItemsAndDatasetsBasedOnDatasetReturnId(activeGroupingTag, itemsWithTags, datasets)

    if (uid) {
      debugFilterStore.datasetAndItemPerSubTagPerId[uid.toString()] = allSubTagsWithItemAndDataset
    }
    const { newDatasets: newSumDatasets, newItems: newSumItems } = getDatasetsAndItemsPerTag({
      activeGroupingTag,
      allSubTagsWithItemAndDataset,
      tags_config,
    })
    return { datasets: newSumDatasets, items: newSumItems }
  }
}
